import React, { useMemo, useState } from 'react';
import type { InspectorEnums } from './ElementInspectorFields';
import { defaultBeatOrder, enumDisplayLabel } from '../utils/enumTranslator';

type Locale = 'zh' | 'en';
type LaneGroupMode = 'structure' | 'episode';

interface LaneViewProps {
  dramaElements: any[];
  onElementClick?: (element: any) => void;
  locale?: Locale;
  enumOptions?: InspectorEnums;
}

const extractEpisodeLabel = (value: any) => {
  const label = String(value || '').trim();
  if (!label) {
    return '';
  }
  return label;
};

const episodeSortValue = (value: string) => {
  const match = value.match(/(\d+)/);
  if (!match) {
    return Number.MAX_SAFE_INTEGER;
  }
  return Number(match[1]);
};

const COPY = {
  zh: {
    title: '剧本结构视图',
    subtitle: '按项目实际结构、场景与节拍查看推进。当前视图用于浏览和定位，不直接编辑元素。',
    readOnlyBadge: '只读结构浏览',
    readOnlyHint: '点击场景或事件卡片会联动右侧检查器；批量编辑、拖动与连线请切到 Canvas。',
    overviewTitle: '戏剧要素概览',
    laneGrouping: '泳道分组',
    byAct: '按结构单元',
    byEpisode: '按集',
    expand: '展开',
    collapse: '收起',
    characters: '人物',
    themes: '主题',
    plotDevices: '情节装置',
    arcMilestones: '角色弧里程碑',
    worldview: '世界观',
    location: '地点',
    time: '时间',
    unknown: '未知',
    missingNote: '未设置说明',
    sceneCount: (count: number) => `${count} 个场景`,
    unnamedScene: '未命名场景',
    purpose: '目的',
    episode: '集数',
    beatTimeline: '节拍时间线',
    beats: '节拍',
    role: '角色',
    emotion: '情绪',
    noEvents: '暂无事件',
    noScenes: '暂无场景数据',
    noScenesHint: '请先创建场景，或先运行结构生成。',
    inspectHint: '点击卡片可在右侧检查器中查看详情',
    stage: '幕',
    unassignedEpisode: '未分集',
  },
  en: {
    title: 'Structure Lanes',
    subtitle: 'Review the story by its actual structure, scenes, and beats. This view is for browsing and inspection, not direct editing.',
    readOnlyBadge: 'Read-only structure view',
    readOnlyHint: 'Click a scene or event card to inspect it on the right. Use Canvas for editing, dragging, and linking.',
    overviewTitle: 'Dramatic Overview',
    laneGrouping: 'Lane grouping',
    byAct: 'By structure',
    byEpisode: 'By episode',
    expand: 'Expand',
    collapse: 'Collapse',
    characters: 'Characters',
    themes: 'Themes',
    plotDevices: 'Plot devices',
    arcMilestones: 'Arc milestones',
    worldview: 'Worldview',
    location: 'Location',
    time: 'Time',
    unknown: 'Unknown',
    missingNote: 'No detail set',
    sceneCount: (count: number) => `${count} scenes`,
    unnamedScene: 'Untitled scene',
    purpose: 'Purpose',
    episode: 'Episode',
    beatTimeline: 'Beat timeline',
    beats: 'Beat',
    role: 'Role',
    emotion: 'Emotion',
    noEvents: 'No events yet',
    noScenes: 'No scenes available',
    noScenesHint: 'Create scenes first or run structure generation.',
    inspectHint: 'Click a card to inspect it in the right panel',
    stage: 'Act',
    unassignedEpisode: 'Unassigned episode',
  },
} as const;

const structureKey = (data: any) => String(
  data?.story_phase ?? data?.phase ?? data?.chapter ?? data?.sequence ??
  data?.act ?? data?.act_type ?? data?.actType ?? '场景序列'
).trim() || '场景序列';

const compact = (value: any, limit = 84) => {
  const text = String(value || '').trim().replace(/\s+/g, ' ');
  if (!text) return '';
  if (text.length <= limit) return text;
  return `${text.slice(0, limit).trimEnd()}...`;
};

const listText = (value: any, limit = 4) => {
  const items = Array.isArray(value) ? value : [];
  return items
    .map((item) => String(item || '').trim())
    .filter(Boolean)
    .slice(0, limit)
    .join(' · ');
};

const extractCharacterStateSnapshots = (dramaElements: any[]) => {
  const scenes = dramaElements.filter((element) => element.type === 'scene');
  const snapshots = new Map<string, { episode: string; action: string; emotion: string; sourceScene: string }>();

  const upsert = (name: string, payload: { episode: string; action: string; emotion: string; sourceScene: string }) => {
    const key = String(name || '').trim();
    if (!key) return;
    const existing = snapshots.get(key);
    if (!existing || episodeSortValue(payload.episode) >= episodeSortValue(existing.episode)) {
      snapshots.set(key, payload);
    }
  };

  scenes.forEach((scene) => {
    const sceneData = scene?.data || {};
    const episode = extractEpisodeLabel(sceneData?.episode) || '0';
    const sourceScene = String(scene?.name || scene?.title || scene?.id || '').trim();
    const states = sceneData?.character_states;
    if (Array.isArray(states)) {
      states.forEach((state: any) => {
        const name = String(state?.name || state?.character_name || state?.character || '').trim();
        if (!name) return;
        upsert(name, {
          episode,
          sourceScene,
          action: String(
            state?.action_state || state?.current_action || state?.action || state?.intent || state?.next_intent || ''
          ).trim(),
          emotion: String(
            state?.emotion || state?.emotional_state || state?.psychological_state || state?.mood || ''
          ).trim(),
        });
      });
      return;
    }

    if (states && typeof states === 'object') {
      Object.entries(states).forEach(([name, state]: any) => {
        if (!name) return;
        upsert(name, {
          episode,
          sourceScene,
          action: String(
            state?.action_state || state?.current_action || state?.action || state?.intent || state?.next_intent || ''
          ).trim(),
          emotion: String(
            state?.emotion || state?.emotional_state || state?.psychological_state || state?.mood || ''
          ).trim(),
        });
      });
    }
  });

  return snapshots;
};

const detailRowsForElement = (item: any, locale: Locale) => {
  const rows: Array<{ label: string; value: string }> = [];
  const data = item?.data || {};
  const zh = locale === 'zh';
  const type = String(item?.type || '');

  if (type === 'character') {
    if (data.character_type) rows.push({ label: zh ? '定位' : 'Role', value: compact(data.character_type, 32) });
    if (data.goal) rows.push({ label: zh ? '目标' : 'Goal', value: compact(data.goal, 68) });
    if (data.obstacle) rows.push({ label: zh ? '阻碍' : 'Obstacle', value: compact(data.obstacle, 68) });
    if (data.arc) rows.push({ label: zh ? '弧线' : 'Arc', value: compact(data.arc, 72) });
    if (Array.isArray(data.personality_traits) && data.personality_traits.length) {
      rows.push({ label: zh ? '特质' : 'Traits', value: listText(data.personality_traits, 5) });
    }
  } else if (type === 'theme') {
    if (data.description || item.description) rows.push({ label: zh ? '说明' : 'Note', value: compact(data.description || item.description, 84) });
    if (Array.isArray(data.examples) && data.examples.length) rows.push({ label: zh ? '例子' : 'Examples', value: listText(data.examples, 3) });
    if (Array.isArray(data.related_characters) && data.related_characters.length) rows.push({ label: zh ? '关联角色' : 'Characters', value: listText(data.related_characters, 4) });
  } else if (type === 'plot_device' || type === 'device') {
    if (data.device_type) rows.push({ label: zh ? '类型' : 'Type', value: compact(data.device_type, 32) });
    if (data.short_drama_function) rows.push({ label: zh ? '短剧作用' : 'Drama use', value: compact(data.short_drama_function, 32) });
    if (Array.isArray(data.short_drama_tags) && data.short_drama_tags.length) rows.push({ label: zh ? '标签' : 'Tags', value: listText(data.short_drama_tags, 4) });
    if (Array.isArray(data.recommended_catharsis_types) && data.recommended_catharsis_types.length) rows.push({ label: zh ? '爽点型' : 'Catharsis', value: listText(data.recommended_catharsis_types, 3) });
    if (Array.isArray(data.recommended_reversal_types) && data.recommended_reversal_types.length) rows.push({ label: zh ? '反转型' : 'Reversal', value: listText(data.recommended_reversal_types, 3) });
    if (data.narrative_layer) rows.push({ label: zh ? '层级' : 'Layer', value: compact(data.narrative_layer, 32) });
    if (data.description) rows.push({ label: zh ? '说明' : 'Description', value: compact(data.description, 84) });
    if (Array.isArray(data.expected_evidence) && data.expected_evidence.length) rows.push({ label: zh ? '证据' : 'Evidence', value: listText(data.expected_evidence, 3) });
    if (Array.isArray(data.setup_nodes) && data.setup_nodes.length) rows.push({ label: zh ? 'Setup' : 'Setup', value: `${data.setup_nodes.length} ${zh ? '处' : 'nodes'}` });
    if (Array.isArray(data.payoff_nodes) && data.payoff_nodes.length) rows.push({ label: zh ? 'Payoff' : 'Payoff', value: `${data.payoff_nodes.length} ${zh ? '处' : 'nodes'}` });
  } else if (type === 'arc_milestone') {
    if (data.milestone_type) rows.push({ label: zh ? '里程碑' : 'Milestone', value: compact(data.milestone_type, 36) });
    if (data.note) rows.push({ label: zh ? '说明' : 'Note', value: compact(data.note, 84) });
    if (data.attached_beat) rows.push({ label: zh ? '节拍' : 'Beat', value: compact(data.attached_beat, 36) });
  } else if (type === 'world' || type === 'world_state') {
    if (data.genre) rows.push({ label: zh ? '类型' : 'Genre', value: compact(data.genre, 32) });
    if (data.location) rows.push({ label: zh ? '地点' : 'Location', value: compact(data.location, 42) });
    if (data.current_time || data.time_period) rows.push({ label: zh ? '时间' : 'Time', value: compact(data.current_time || data.time_period, 42) });
    if (Array.isArray(data.rules) && data.rules.length) rows.push({ label: zh ? '规则' : 'Rules', value: listText(data.rules, 3) });
    if (data.social_context) rows.push({ label: zh ? '社会背景' : 'Context', value: compact(data.social_context, 84) });
  }

  return rows.slice(0, 4);
};

const LaneView: React.FC<LaneViewProps> = ({ dramaElements, onElementClick, locale = 'zh', enumOptions }) => {
  const [overviewCollapsed, setOverviewCollapsed] = useState(false);
  const [groupMode, setGroupMode] = useState<LaneGroupMode>('structure');
  const text = COPY[locale];
  const beatOrder = defaultBeatOrder(enumOptions);
  const characterSnapshots = useMemo(() => extractCharacterStateSnapshots(dramaElements), [dramaElements]);

  const { organizedByAct, organizedByEpisode, characters, themes, plotDevices, arcMilestones, worldview } = useMemo(() => {
    const next: Record<string, Array<{ scene: any; events: any[] }>> = {};
    const nextEpisodes: Record<string, Array<{ scene: any; events: any[] }>> = {};

    const scenes = dramaElements.filter((element) => element.type === 'scene');
    const events = dramaElements.filter((element) => element.type === 'event');

    scenes.forEach((scene) => {
      const sceneId = String(scene.id || scene.data?.backendId || '');
      const act = structureKey(scene.data);
      const sceneEvents = events
        .filter((event) => String(event.data?.sceneId || event.data?.scene_id || '') === sceneId)
        .sort((left, right) => {
          return beatOrder.indexOf(left.data?.beat || '') - beatOrder.indexOf(right.data?.beat || '');
        });
      const episode = extractEpisodeLabel(scene.data?.episode) || text.unassignedEpisode;
      nextEpisodes[episode] = nextEpisodes[episode] || [];
      next[act] = next[act] || [];
      next[act].push({ scene, events: sceneEvents });
      nextEpisodes[episode].push({ scene, events: sceneEvents });
    });

    return {
      organizedByAct: next,
      organizedByEpisode: nextEpisodes,
      characters: dramaElements.filter((element) => element.type === 'character'),
      themes: dramaElements.filter((element) => element.type === 'theme'),
      plotDevices: dramaElements.filter((element) => element.type === 'plot_device' || element.type === 'device'),
      arcMilestones: dramaElements.filter((element) => element.type === 'arc_milestone'),
      worldview: dramaElements.find((element) => element.type === 'world' || element.type === 'world_state'),
    };
  }, [beatOrder, dramaElements, text.unassignedEpisode]);

  const hasScenes = Object.values(organizedByAct).some((items) => items.length > 0);
  const orderedEpisodeKeys = useMemo(() => {
    return Object.keys(organizedByEpisode).sort((left, right) => {
      return episodeSortValue(left) - episodeSortValue(right) || left.localeCompare(right);
    });
  }, [organizedByEpisode]);
  const orderedStructureKeys = useMemo(() => Object.keys(organizedByAct).sort((left, right) => {
    const leftNumber = Number(left.match(/(\d+)/)?.[1] || Number.MAX_SAFE_INTEGER);
    const rightNumber = Number(right.match(/(\d+)/)?.[1] || Number.MAX_SAFE_INTEGER);
    return leftNumber - rightNumber || left.localeCompare(right);
  }), [organizedByAct]);

  const summaryCards = [
    {
      key: 'characters',
      title: text.characters,
      items: characters,
      icon: '👤',
    },
    {
      key: 'themes',
      title: text.themes,
      items: themes,
      icon: '💭',
    },
    {
      key: 'plotDevices',
      title: text.plotDevices,
      items: plotDevices,
      icon: '🔧',
    },
    {
      key: 'arcMilestones',
      title: text.arcMilestones,
      items: arcMilestones,
      icon: '🧭',
    },
  ];

  const laneSections =
    groupMode === 'episode'
      ? orderedEpisodeKeys.map((episode) => ({
          key: episode,
          title: episode,
          scenes: organizedByEpisode[episode] || [],
        }))
      : orderedStructureKeys.map((act) => ({
          key: act,
          title: /^act_/i.test(act) ? enumDisplayLabel(act, locale, 'act') : act,
          scenes: organizedByAct[act] || [],
        }));

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        overflow: 'auto',
        background: 'linear-gradient(180deg, #fbf7ef 0%, #f1ede4 100%)',
        padding: '16px',
        fontFamily: "'Noto Serif SC', 'Source Serif 4', serif",
        color: '#1a1a1a',
      }}
    >
      <div
        style={{
          width: '100%',
          minWidth: 1180,
          display: 'grid',
          gridTemplateRows: 'auto auto minmax(0, 1fr)',
          gap: '14px',
        }}
      >
        <section
          style={{
            background: 'rgba(255,255,255,0.88)',
            border: '1px solid rgba(32, 26, 18, 0.12)',
            borderRadius: 18,
            padding: '14px 16px',
            boxShadow: '0 12px 28px rgba(59, 40, 17, 0.05)',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 16,
              flexWrap: 'wrap',
            }}
          >
            <div>
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '6px 10px',
                  background: 'rgba(44, 64, 32, 0.08)',
                  borderRadius: 999,
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                }}
              >
                {text.readOnlyBadge}
              </div>
              <h2 style={{ margin: '10px 0 6px', fontSize: 23, lineHeight: 1.15 }}>{text.title}</h2>
              <p style={{ margin: 0, maxWidth: 940, color: '#5a5347', lineHeight: 1.45, fontSize: 13 }}>{text.subtitle}</p>
            </div>
            <div
              style={{
                minWidth: 260,
                maxWidth: 560,
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr) 170px',
                alignItems: 'center',
                gap: 12,
                padding: '10px 12px',
                borderRadius: 14,
                background: 'rgba(141, 109, 62, 0.08)',
                border: '1px solid rgba(141, 109, 62, 0.16)',
                color: '#594228',
                fontSize: 13,
                lineHeight: 1.6,
              }}
            >
              <div>{text.readOnlyHint}</div>
              <label style={{ display: 'grid', gap: 6 }}>
                <span style={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{text.laneGrouping}</span>
                <select
                  value={groupMode}
                  onChange={(event) => setGroupMode(event.target.value as LaneGroupMode)}
                  style={{
                    minHeight: 40,
                    borderRadius: 10,
                    border: '1px solid rgba(141, 109, 62, 0.18)',
                    background: 'rgba(255,255,255,0.92)',
                    color: '#3f2f1b',
                    padding: '0 12px',
                    cursor: 'pointer',
                  }}
                >
                  <option value="structure">{text.byAct}</option>
                  <option value="episode">{text.byEpisode}</option>
                </select>
              </label>
            </div>
          </div>
        </section>

        {(characters.length > 0 || themes.length > 0 || plotDevices.length > 0 || arcMilestones.length > 0 || worldview) && (
          <section
            style={{
              background: 'rgba(255,255,255,0.92)',
              border: '1px solid rgba(32, 26, 18, 0.1)',
              borderRadius: 18,
              padding: '14px 16px',
              boxShadow: '0 12px 28px rgba(59, 40, 17, 0.04)',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 12,
                marginBottom: overviewCollapsed ? 0 : 12,
              }}
            >
              <h3 style={{ margin: 0, fontSize: 18 }}>{text.overviewTitle}</h3>
              <button
                type="button"
                onClick={() => setOverviewCollapsed((value) => !value)}
                title={overviewCollapsed ? text.expand : text.collapse}
                style={{
                  border: '1px solid rgba(32, 26, 18, 0.14)',
                  background: '#fff',
                  borderRadius: 999,
                  padding: '8px 14px',
                  cursor: 'pointer',
                  fontSize: 13,
                }}
              >
                {overviewCollapsed ? text.expand : text.collapse}
              </button>
            </div>

            {!overviewCollapsed && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10, alignItems: 'stretch' }}>
                {summaryCards.map((card) =>
                  card.items.length ? (
                    <div
                      key={card.key}
                      style={{
                        background: '#fbfaf6',
                        border: '1px solid rgba(32, 26, 18, 0.09)',
                        borderRadius: 14,
                        padding: 12,
                      }}
                    >
                      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>
                        {card.icon} {card.title} ({card.items.length})
                      </div>
                      <div style={{ display: 'grid', gap: 8, maxHeight: 260, overflowY: 'auto' }}>
                        {card.items.map((item: any) => (
                          <button
                            key={item.id}
                            type="button"
                            onClick={() => onElementClick?.(item)}
                            style={{
                              textAlign: 'left',
                              border: '1px solid rgba(32, 26, 18, 0.08)',
                              background: '#fff',
                              borderRadius: 12,
                              padding: '10px 11px',
                              cursor: 'pointer',
                            }}
                          >
                            <div style={{ fontWeight: 700, marginBottom: 8 }}>{item.name || item.title || item.id}</div>
                            {card.key === 'characters' && (
                              <div style={{ display: 'grid', gap: 5, marginBottom: 8, fontSize: 12, color: '#5d5548' }}>
                                {(() => {
                                  const snapshot =
                                    characterSnapshots.get(String(item.name || '').trim()) ||
                                    characterSnapshots.get(String(item.data?.name || '').trim());
                                  if (!snapshot) return null;
                                  return (
                                    <>
                                      {snapshot.action ? (
                                        <div>
                                          <strong>{locale === 'zh' ? '当前行动' : 'Current action'}:</strong> {compact(snapshot.action, 90)}
                                        </div>
                                      ) : null}
                                      {snapshot.emotion ? (
                                        <div>
                                          <strong>{locale === 'zh' ? '心理/情绪' : 'Psych state'}:</strong> {compact(snapshot.emotion, 90)}
                                        </div>
                                      ) : null}
                                      <div style={{ color: '#7a715f' }}>
                                        {locale === 'zh' ? '最近场景' : 'Latest scene'}: {snapshot.sourceScene || '-'}
                                        {snapshot.episode ? ` · ${text.episode}: ${snapshot.episode}` : ''}
                                      </div>
                                    </>
                                  );
                                })()}
                              </div>
                            )}
                            <div style={{ display: 'grid', gap: 5 }}>
                              {detailRowsForElement(item, locale).length ? (
                                detailRowsForElement(item, locale).map((row) => (
                                  <div key={`${item.id}-${row.label}`} style={{ display: 'grid', gridTemplateColumns: '72px 1fr', gap: 8, fontSize: 12, color: '#5d5548' }}>
                                    <span style={{ color: '#8b7f69', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{row.label}</span>
                                    <span>{row.value}</span>
                                  </div>
                                ))
                              ) : (
                                <div style={{ fontSize: 12, color: '#6d6558' }}>{text.missingNote}</div>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : null
                )}

                {worldview && (
                  <button
                    type="button"
                    onClick={() => onElementClick?.(worldview)}
                    style={{
                      textAlign: 'left',
                      border: '1px solid rgba(32, 26, 18, 0.09)',
                      background: '#fbfaf6',
                      borderRadius: 14,
                      padding: 12,
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>🌍 {text.worldview}</div>
                    <div style={{ display: 'grid', gap: 8, fontSize: 13, color: '#4b463d' }}>
                      <div>
                        <strong>{text.location}:</strong> {worldview.data?.location || text.unknown}
                      </div>
                      <div>
                        <strong>{text.time}:</strong> {worldview.data?.current_time || worldview.data?.time_period || text.unknown}
                      </div>
                      {worldview.data?.genre && <div><strong>{locale === 'zh' ? '类型' : 'Genre'}:</strong> {worldview.data.genre}</div>}
                      {Array.isArray(worldview.data?.rules) && worldview.data.rules.length > 0 && (
                        <div><strong>{locale === 'zh' ? '规则' : 'Rules'}:</strong> {listText(worldview.data.rules, 4)}</div>
                      )}
                      {worldview.data?.social_context && <div>{worldview.data.social_context}</div>}
                    </div>
                  </button>
                )}
              </div>
            )}
          </section>
        )}

        <div
          style={{
            display: 'grid',
            gridAutoFlow: 'column',
            gridAutoColumns: groupMode === 'episode' ? 'minmax(340px, 1fr)' : 'minmax(360px, 1fr)',
            gap: 12,
            alignItems: 'stretch',
            overflowX: 'auto',
            paddingBottom: 4,
          }}
        >
        {laneSections.map((section, index) => {
          const scenes = section.scenes || [];
          return (
            <section
              key={section.key}
              style={{
                background: 'rgba(255,255,255,0.94)',
                border: '1px solid rgba(32, 26, 18, 0.1)',
                borderRadius: 18,
                padding: 14,
                minHeight: 520,
                boxShadow: '0 12px 28px rgba(59, 40, 17, 0.045)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 16,
                  flexWrap: 'wrap',
                  marginBottom: 12,
                  paddingBottom: 10,
                  borderBottom: '1px solid rgba(32, 26, 18, 0.08)',
                }}
              >
                <div style={{ fontSize: 18, fontWeight: 700 }}>{section.title}</div>
                <div style={{ fontSize: 13, color: '#6d6558' }}>
                  {text.sceneCount(scenes.length)}
                  {onElementClick ? ` · ${text.inspectHint}` : ''}
                </div>
              </div>

              {scenes.length === 0 && (
                <div
                  style={{
                    padding: '18px 0 2px',
                    fontSize: 14,
                    color: '#847a68',
                    fontStyle: 'italic',
                  }}
                >
                  {text.noScenes}
                </div>
              )}

              <div style={{ display: 'grid', gap: 10 }}>
                {scenes.map(({ scene, events }) => (
                  <article
                    key={scene.id}
                    onClick={() => onElementClick?.(scene)}
                    style={{
                      border: '1px solid rgba(32, 26, 18, 0.1)',
                      background: '#fff',
                      borderRadius: 14,
                      padding: 14,
                      cursor: onElementClick ? 'pointer' : 'default',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
                          🎭 {scene.name || text.unnamedScene}
                        </div>
                        <div style={{ fontSize: 13, color: '#6d6558', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                          {scene.data?.episode && <span>{text.episode}: {scene.data.episode}</span>}
                          {scene.data?.location && <span>📍 {scene.data.location}</span>}
                          {scene.data?.purpose && (
                            <span>
                              {text.purpose}: {scene.data.purpose}
                            </span>
                          )}
                        </div>
                        <div style={{ marginTop: 10, display: 'grid', gap: 6, fontSize: 12, color: '#5c5446' }}>
                          {scene.data?.conflict && <div><strong>{locale === 'zh' ? '冲突' : 'Conflict'}:</strong> {compact(scene.data.conflict, 120)}</div>}
                          {(scene.data?.hook_type || scene.data?.bang_type || scene.data?.cliff_type) && (
                            <div>
                              <strong>{locale === 'zh' ? '机制' : 'Mechanics'}:</strong>{' '}
                              {[scene.data?.hook_type, scene.data?.bang_type, scene.data?.cliff_type].filter(Boolean).join(' · ')}
                            </div>
                          )}
                          {Array.isArray(scene.data?.characters_present) && scene.data.characters_present.length > 0 && (
                            <div><strong>{locale === 'zh' ? '在场角色' : 'Present'}:</strong> {listText(scene.data.characters_present, 5)}</div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div
                      style={{
                        marginTop: 12,
                        padding: 10,
                        borderRadius: 12,
                        background: '#faf7f0',
                        border: '1px solid rgba(32, 26, 18, 0.07)',
                      }}
                    >
                      <div style={{ fontWeight: 700, marginBottom: 8, fontSize: 13 }}>{text.beatTimeline}</div>
                      <div style={{ display: 'grid', gap: 7 }}>
                        {events.length ? (
                          events.map((event) => {
                            const eventData = event?.data || {};
                            const eventDesc = compact(
                              eventData?.description || eventData?.summary || eventData?.detail || eventData?.note || '',
                              160
                            );
                            const eventImpact = compact(
                              eventData?.impact || eventData?.effect || eventData?.outcome || '',
                              100
                            );
                            const eventActors = listText(
                              eventData?.characters_involved || eventData?.characters || eventData?.participants || [],
                              5
                            );
                            return (
                              <button
                                key={event.id}
                                type="button"
                                onClick={(clickEvent) => {
                                  clickEvent.stopPropagation();
                                  onElementClick?.(event);
                                }}
                                style={{
                                  textAlign: 'left',
                                  border: '1px solid rgba(32, 26, 18, 0.08)',
                                  background: '#fff',
                                  borderRadius: 10,
                                  padding: '9px 10px',
                                  fontSize: 12,
                                  cursor: 'pointer',
                                  display: 'grid',
                                  gap: 5,
                                }}
                                title={event.name}
                              >
                                <div>
                                  <strong>{event.name || eventData?.event_name || event.id}</strong>
                                  {eventData?.beat && (
                                    <span style={{ color: '#726853' }}>
                                      {' · '}
                                      {enumDisplayLabel(String(eventData.beat || ''), locale, 'beat')}
                                    </span>
                                  )}
                                </div>
                                {eventDesc && (
                                  <div style={{ color: '#5c5446' }}>
                                    <strong>{locale === 'zh' ? '事件' : 'Detail'}:</strong> {eventDesc}
                                  </div>
                                )}
                                {eventImpact && (
                                  <div style={{ color: '#5c5446' }}>
                                    <strong>{locale === 'zh' ? '影响' : 'Impact'}:</strong> {eventImpact}
                                  </div>
                                )}
                                {eventActors && (
                                  <div style={{ color: '#5c5446' }}>
                                    <strong>{locale === 'zh' ? '涉及角色' : 'Actors'}:</strong> {eventActors}
                                  </div>
                                )}
                              </button>
                            );
                          })
                        ) : (
                          <div style={{ fontSize: 12, color: '#8a816f', fontStyle: 'italic' }}>{text.noEvents}</div>
                        )}
                      </div>
                    </div>

                    {Array.isArray(scene.data?.beat_list?.beats) && scene.data.beat_list.beats.length > 0 && (
                      <div style={{ marginTop: 14, display: 'grid', gap: 8 }}>
                        {scene.data.beat_list.beats.map((beat: string, beatIndex: number) => (
                          <div
                            key={`${scene.id}-beat-${beatIndex}`}
                            style={{
                              borderLeft: '3px solid rgba(129, 98, 59, 0.5)',
                              paddingLeft: 10,
                              fontSize: 12,
                              color: '#5b5448',
                              lineHeight: 1.55,
                            }}
                          >
                            <strong>
                              {text.beats} {beatIndex + 1}
                            </strong>
                            {' · '}
                            {beat}
                          </div>
                        ))}
                      </div>
                    )}
                  </article>
                ))}
              </div>
            </section>
          );
        })}
        </div>

        {!hasScenes && (
          <section
            style={{
              background: 'rgba(255,255,255,0.94)',
              border: '1px solid rgba(32, 26, 18, 0.1)',
              borderRadius: 24,
              padding: '48px 24px',
              textAlign: 'center',
              boxShadow: '0 18px 40px rgba(59, 40, 17, 0.05)',
            }}
          >
            <p style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>{text.noScenes}</p>
            <p style={{ margin: '10px 0 0', color: '#726853', lineHeight: 1.6 }}>{text.noScenesHint}</p>
          </section>
        )}
      </div>
    </div>
  );
};

export default LaneView;
